import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Button,
  Caption1,
  Text,
  SearchBox,
  Dropdown,
  Option,
  Field,
  Spinner,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components'
import {
  ImageMultipleRegular,
  ImageAddRegular,
  DocumentRegular,
  LinkRegular,
  EyeRegular,
} from '@fluentui/react-icons'
import { Asset, AssetFileType } from '@templatean/shared/types'
import { assetService } from '@templatean/shared/services'
import { formatFileSize } from '@templatean/shared/utils'
import { useNotify } from '../hooks/useNotify'
import { EmptyState, ErrorState, SectionHeader } from './ui'

interface AssetBrowserProps {
  onSelect: (asset: Asset) => void
}

/** Tiles per page; the grid stays scannable without a long scroll. */
const PAGE_SIZE = 12

const TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'image/jpeg', label: 'JPEG images' },
  { value: 'image/png', label: 'PNG images' },
  { value: 'image/gif', label: 'GIF images' },
  { value: 'text/plain', label: 'Text files' },
  { value: 'text/html', label: 'HTML files' },
]

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalL },
  filters: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS },
  full: { width: '100%', minWidth: 0 },
  // Two tiles at pane width, one when the user drags the pane very narrow.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  tile: {
    position: 'relative',
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.overflow('hidden'),
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    ...shorthands.padding(0),
    display: 'block',
    width: '100%',
    textAlign: 'left',
    ':hover': { ...shorthands.borderColor(tokens.colorBrandStroke1) },
    ':focus-visible': {
      ...shorthands.outline('2px', 'solid', tokens.colorStrokeFocus2),
      outlineOffset: '1px',
    },
  },
  thumb: {
    position: 'relative',
    width: '100%',
    aspectRatio: '1 / 1',
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.overflow('hidden'),
  },
  image: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  // The overlay names the action rather than only dimming the tile.
  overlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: tokens.spacingVerticalXS,
    backgroundColor: 'rgba(0, 0, 0, 0.58)',
    color: tokens.colorNeutralForegroundOnBrand,
    opacity: 0,
    transitionProperty: 'opacity',
    transitionDuration: tokens.durationFaster,
    '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0s' },
  },
  overlayVisible: { opacity: 1 },
  overlayLabel: { color: tokens.colorNeutralForegroundOnBrand, fontWeight: tokens.fontWeightSemibold },
  caption: {
    ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalS),
    display: 'flex',
    flexDirection: 'column',
  },
  name: {
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalXS,
  },
})

const isImage = (asset: Asset) => asset.fileType?.startsWith('image/')

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

const AssetBrowser: React.FC<AssetBrowserProps> = ({ onSelect }) => {
  const styles = useStyles()
  const notify = useNotify()
  const [assets, setAssets] = useState<Asset[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState('all')
  const [visible, setVisible] = useState(PAGE_SIZE)
  const [insertingId, setInsertingId] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)

  const loadAssets = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await assetService.getAssets({
        fileTypes: selectedType === 'all' ? undefined : [selectedType as AssetFileType],
        pageSize: 60,
      })
      setAssets(response.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The asset library did not respond.')
    } finally {
      setLoading(false)
    }
  }, [selectedType])

  useEffect(() => {
    void loadAssets()
  }, [loadAssets])

  const filtered = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return assets
    return assets.filter(
      asset =>
        asset.name.toLowerCase().includes(query) ||
        (asset.description?.toLowerCase().includes(query) ?? false) ||
        asset.tags.some(tag => tag.toLowerCase().includes(query))
    )
  }, [assets, searchQuery])

  useEffect(() => setVisible(PAGE_SIZE), [searchQuery, selectedType])

  const shown = filtered.slice(0, visible)

  const handleInsert = async (asset: Asset) => {
    if (insertingId) return

    setInsertingId(asset.id)
    try {
      const replaced = await Word.run(async context => {
        // Insert where the cursor is, not at the end of the document. Replacing the
        // range means a selected picture or passage is swapped for the new asset
        // rather than the new one being appended somewhere else entirely.
        const selection = context.document.getSelection()
        selection.load('text')
        const pictures = selection.inlinePictures
        pictures.load('items')
        await context.sync()

        const replacing = pictures.items.length > 0 || selection.text.length > 0

        if (isImage(asset)) {
          const response = await fetch(asset.url)
          if (!response.ok) throw new Error(`The image could not be downloaded (${response.status}).`)
          const base64 = await blobToBase64(await response.blob())
          selection.insertInlinePictureFromBase64(base64.split(',')[1], Word.InsertLocation.replace)
        } else if (asset.fileType === 'text/plain' || asset.fileType === 'text/html') {
          const response = await fetch(asset.url)
          if (!response.ok) throw new Error(`The file could not be downloaded (${response.status}).`)
          const text = await response.text()
          if (asset.fileType === 'text/html') {
            selection.insertHtml(text, Word.InsertLocation.replace)
          } else {
            selection.insertText(text, Word.InsertLocation.replace)
          }
        } else {
          selection.insertText(`${asset.name} (${asset.url})`, Word.InsertLocation.replace)
        }

        await context.sync()
        return replacing
      })

      notify({
        title: replaced
          ? 'Selection replaced'
          : isImage(asset)
            ? 'Image inserted'
            : 'Asset inserted',
        body: asset.name,
      })
      onSelect(asset)
    } catch (err) {
      notify({
        intent: 'error',
        title: 'Could not insert the asset',
        body: err instanceof Error ? err.message : 'Word refused the request.',
      })
    } finally {
      setInsertingId(null)
    }
  }

  if (loading) {
    return (
      <div className={styles.root}>
        <Spinner size="small" label="Loading assets" labelPosition="below" />
      </div>
    )
  }

  if (error) {
    return <ErrorState title="Assets unavailable" detail={error} onRetry={loadAssets} />
  }

  return (
    <div className={styles.root}>
      <div className={styles.filters}>
        <SearchBox
          className={styles.full}
          placeholder="Search assets"
          aria-label="Search assets"
          value={searchQuery}
          onChange={(_, data) => setSearchQuery(data.value)}
        />
        <Field label="File type">
          <Dropdown
            className={styles.full}
            selectedOptions={[selectedType]}
            value={TYPE_OPTIONS.find(o => o.value === selectedType)?.label ?? 'All types'}
            onOptionSelect={(_, data) => setSelectedType(data.optionValue ?? 'all')}
          >
            {TYPE_OPTIONS.map(option => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Dropdown>
        </Field>
      </div>

      <div>
        <SectionHeader action={<Caption1>{filtered.length}</Caption1>}>Assets</SectionHeader>

        {filtered.length === 0 ? (
          <EmptyState
            icon={<ImageMultipleRegular fontSize={32} primaryFill={tokens.colorNeutralForeground3} />}
            title="No assets here yet"
            hint="Upload images and files in Templatean, then insert them into your document from this pane."
          />
        ) : (
          <div className={styles.grid}>
            {shown.map(asset => {
              const inserting = insertingId === asset.id
              const showOverlay = hoveredId === asset.id || inserting

              return (
                <Tooltip key={asset.id} content={asset.name} relationship="label" withArrow>
                  <button
                    type="button"
                    className={styles.tile}
                    onClick={() => handleInsert(asset)}
                    onMouseEnter={() => setHoveredId(asset.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onFocus={() => setHoveredId(asset.id)}
                    onBlur={() => setHoveredId(null)}
                    disabled={insertingId !== null && !inserting}
                  >
                    <span className={styles.thumb}>
                      {isImage(asset) ? (
                        <img className={styles.image} src={asset.url} alt="" loading="lazy" />
                      ) : asset.fileType === 'text/html' ? (
                        <LinkRegular fontSize={28} primaryFill={tokens.colorNeutralForeground3} />
                      ) : (
                        <DocumentRegular fontSize={28} primaryFill={tokens.colorNeutralForeground3} />
                      )}

                      <span
                        className={mergeClasses(styles.overlay, showOverlay && styles.overlayVisible)}
                        aria-hidden="true"
                      >
                        {inserting ? (
                          <Spinner size="tiny" appearance="inverted" />
                        ) : (
                          <ImageAddRegular fontSize={22} />
                        )}
                        <Caption1 className={styles.overlayLabel}>
                          {inserting ? 'Inserting' : 'Insert'}
                        </Caption1>
                      </span>
                    </span>

                    <span className={styles.caption}>
                      <Text size={200} weight="semibold" className={styles.name}>
                        {asset.name}
                      </Text>
                      <Caption1>{formatFileSize(asset.fileSize)}</Caption1>
                    </span>
                  </button>
                </Tooltip>
              )
            })}
          </div>
        )}
      </div>

      {visible < filtered.length && (
        <div className={styles.footer}>
          <Button appearance="subtle" icon={<EyeRegular />} onClick={() => setVisible(v => v + PAGE_SIZE)}>
            Show more
          </Button>
          <Caption1>
            Showing {shown.length} of {filtered.length}
          </Caption1>
        </div>
      )}
    </div>
  )
}

export default AssetBrowser
